import React, { useRef } from 'react';
import { CreditCard, HelpCircle } from 'react-feather';

interface WalletItem {
  id: string;
  description: string;
  amount: number;
  date: string;
  accountId?: number | null;
  accountName?: string | null;
}

interface WalletEarmark {
  name: string;
  monthlyShare: number;
  fullAmount: number;
  saved: number;
  dueYear: number;
  dueMonth: number;
  accountId?: number | null;
  accountName?: string | null;
}

interface WalletAccount {
  id: number;
  account_name: string;
  account_type: string;
  balance: number;
}

interface WalletModalProps {
  accounts: WalletAccount[];
  items: WalletItem[];
  earmarks: WalletEarmark[];
  salaryLabel: string | null;
  onClose: () => void;
}

const MONTHS = ['januari', 'februari', 'mars', 'april', 'maj', 'juni', 'juli', 'augusti', 'september', 'oktober', 'november', 'december'];

// The wallet: each account with its balance, the striped share already
// claimed by charges expected before the next salary, and the exact list
// of what will draw from it - including earmarks for yearly/quarterly
// costs building up on the account.
const WalletModal: React.FC<WalletModalProps> = ({ accounts, items, earmarks, salaryLabel, onClose }) => {
  const backdropMouseDown = useRef(false);

  const formatAmount = (amount: number) =>
    new Intl.NumberFormat('sv-SE', { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(amount) + ' kr';

  const dayMonth = (dateStr: string) => {
    const d = new Date(dateStr);
    return `${d.getDate()} ${MONTHS[d.getMonth()].slice(0, 3)}`;
  };

  const knownIds = new Set(accounts.map(a => a.id));
  const itemsFor = (accountId: number) => items.filter(i => i.accountId === accountId);
  const earmarksFor = (accountId: number) => earmarks.filter(e => e.accountId === accountId);
  const unknownItems = items.filter(i => !i.accountId || !knownIds.has(i.accountId));
  const unknownEarmarks = earmarks.filter(e => !e.accountId || !knownIds.has(e.accountId));

  const checking = accounts.filter(a => a.account_type === 'CHECKING');
  const credit = accounts.filter(a => a.account_type === 'CREDIT_CARD');
  const savings = accounts.filter(a => a.account_type !== 'CHECKING' && a.account_type !== 'CREDIT_CARD');

  const itemRow = (item: WalletItem) => (
    <div key={item.id} className="flex items-center justify-between py-1.5 text-sm border-b border-gray-100 last:border-0">
      <span className="flex items-center min-w-0">
        <span className="text-xs text-gray-400 w-14 flex-shrink-0">~{dayMonth(item.date)}</span>
        <span className="text-gray-700 truncate">{(item.description || '').split('→')[0].trim()}</span>
      </span>
      <span className="font-medium text-gray-800 ml-2 flex-shrink-0">{formatAmount(Math.abs(item.amount))}</span>
    </div>
  );

  // covered = how much of the earmark's should-be-saved amount the account
  // balance actually backs right now. null = no account context (unknown).
  const earmarkRow = (e: WalletEarmark, idx: number, covered: number | null = null) => {
    const fullyCovered = covered !== null && covered + 0.5 >= e.saved;
    return (
      <div key={`em-${idx}`} className="flex items-center justify-between py-1.5 text-sm border-b border-gray-100 last:border-0">
        <span className="flex items-center min-w-0">
          <span className="w-2 h-2 rounded-full bg-amber-400 mr-2 flex-shrink-0"></span>
          <span className="text-gray-700 truncate">{e.name}</span>
          <span className="text-xs text-gray-400 ml-1.5 flex-shrink-0">
            {formatAmount(e.fullAmount)} dras i {MONTHS[e.dueMonth - 1]}
          </span>
        </span>
        {covered === null ? (
          <span className="font-medium text-gray-800 ml-2 flex-shrink-0">{formatAmount(e.saved)} öronmärkt</span>
        ) : fullyCovered ? (
          <span className="font-medium text-green-700 ml-2 flex-shrink-0">{formatAmount(e.saved)} täckt</span>
        ) : (
          <span className={`font-medium ml-2 flex-shrink-0 ${covered <= 0 ? 'text-red-600' : 'text-amber-600'}`}>
            {formatAmount(covered)} av {formatAmount(e.saved)} täckt
          </span>
        )}
      </div>
    );
  };

  const accountSection = (acc: WalletAccount) => {
    const accItems = itemsFor(acc.id);
    // Reality check for the earmarks: bills draw first, then the balance
    // that remains backs the earmarks in due-date order. "saved" is what
    // SHOULD be set aside by now - covered is what the money actually backs.
    const accEarmarks = [...earmarksFor(acc.id)]
      .sort((a, b) => (a.dueYear * 12 + a.dueMonth) - (b.dueYear * 12 + b.dueMonth));
    const billsSum = accItems.reduce((s, i) => s + Math.abs(i.amount), 0);
    let allocatable = Math.max(acc.balance - billsSum, 0);
    const coveredByIdx = accEarmarks.map(e => {
      const c = Math.min(allocatable, e.saved);
      allocatable -= c;
      return c;
    });
    const reserved = billsSum + accEarmarks.reduce((s, e) => s + e.saved, 0);
    const overReserved = reserved > acc.balance;
    const reservedPct = acc.balance > 0 ? Math.min((reserved / acc.balance) * 100, 100) : (reserved > 0 ? 100 : 0);
    return (
      <div key={acc.id} className="mb-5 last:mb-0">
        <div className="flex items-baseline justify-between mb-1.5">
          <span className="text-sm font-semibold text-gray-900">{acc.account_name}</span>
          <span className="text-sm font-bold text-gray-900">{formatAmount(acc.balance)}</span>
        </div>
        <div
          className="h-4 rounded-full overflow-hidden relative mb-1.5"
          style={{ backgroundColor: overReserved ? '#FF3B30' : '#34C759' }}
        >
          <div
            className="absolute top-0 left-0 h-full rounded-full"
            style={{
              width: `${reservedPct}%`,
              backgroundImage: 'repeating-linear-gradient(45deg, rgba(229,231,235,0.92) 0 8px, rgba(229,231,235,0.45) 8px 12px)'
            }}
          ></div>
        </div>
        {reserved > 0 && (
          <p className="text-xs text-gray-500 mb-1">
            {formatAmount(reserved)} vikt · {formatAmount(Math.max(acc.balance - reserved, 0))} fritt
            {overReserved && <span className="text-red-600 font-medium"> — täcker inte det som ska dras</span>}
          </p>
        )}
        {accItems.length === 0 && accEarmarks.length === 0 ? (
          <p className="text-xs text-gray-400">Inget väntas dras härifrån före lönen.</p>
        ) : (
          <div>
            {accItems.map(itemRow)}
            {accEarmarks.map((e, i) => earmarkRow(e, i, coveredByIdx[i]))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[60]"
      onMouseDown={e => { backdropMouseDown.current = e.target === e.currentTarget; }}
      onMouseUp={e => {
        if (backdropMouseDown.current && e.target === e.currentTarget) onClose();
        backdropMouseDown.current = false;
      }}
    >
      <div className="bg-white rounded-xl p-5 w-full max-w-md max-h-[85vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <h2 className="text-lg font-bold text-gray-900 mb-1">Plånbok</h2>
        <p className="text-sm text-gray-600 mb-4">
          Vad som väntas dras från varje konto{salaryLabel ? ` fram till lönen ${salaryLabel}` : ''}
        </p>

        <div className="flex-1 overflow-y-auto mb-4">
          {checking.map(accountSection)}
          {savings.map(accountSection)}

          {credit.map(acc => {
            const accItems = itemsFor(acc.id);
            return (
              <div key={acc.id} className="mb-5 last:mb-0 pt-3 border-t border-gray-100">
                <div className="flex items-baseline justify-between mb-1">
                  <span className="inline-flex items-center text-sm font-semibold text-gray-900">
                    <CreditCard size={14} className="mr-1.5 text-gray-400" />{acc.account_name}
                  </span>
                  <span className="text-sm font-bold text-red-600">skuld {formatAmount(acc.balance)}</span>
                </div>
                {accItems.length > 0 ? (
                  <>
                    <p className="text-xs text-gray-500 mb-1">Dras via kortet — hamnar i kortskulden:</p>
                    {accItems.map(itemRow)}
                  </>
                ) : (
                  <p className="text-xs text-gray-400">Inga väntade dragningar via kortet.</p>
                )}
              </div>
            );
          })}

          {(unknownItems.length > 0 || unknownEarmarks.length > 0) && (
            <div className="pt-3 border-t border-gray-100">
              <p className="inline-flex items-center text-sm font-semibold text-gray-900 mb-1">
                <HelpCircle size={14} className="mr-1.5 text-gray-400" />Konto okänt ännu
              </p>
              <p className="text-xs text-gray-500 mb-1">Kontot lärs in när dragningen bokförts första gången.</p>
              {unknownItems.map(itemRow)}
              {unknownEarmarks.map((e, i) => earmarkRow(e, i))}
            </div>
          )}
        </div>

        <button
          onClick={onClose}
          className="w-full bg-gray-100 text-gray-700 py-3 rounded-xl font-semibold hover:bg-gray-200 transition-colors"
        >
          Stäng
        </button>
      </div>
    </div>
  );
};

export default WalletModal;
